'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { trpc } from '@/lib/api/trpc';
import { cn } from '@/lib/utils';

import type { BulkEditRow } from '../bulk-edit.types';

interface TaxCategory {
  id: string;
  slug: string;
  label: string;
  taxjarCode: string | null;
}

const DEBOUNCE_MS = 300;

export function BulkEditTaxCategoryCell({
  row,
  onChange,
}: {
  row: BulkEditRow;
  onChange: (value: string | null) => void;
}) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [displayLabel, setDisplayLabel] = useState(row.mappedTaxCategory?.label ?? '');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const needsResolve = !!row.mappedTaxCategoryId && !displayLabel && !row.mappedTaxCategory;
  const { data: resolved, isLoading: isResolving } = trpc.taxCategories.getById.useQuery(
    { id: row.mappedTaxCategoryId! },
    { enabled: needsResolve },
  );

  useEffect(() => {
    if (row.mappedTaxCategory) {
      setDisplayLabel(row.mappedTaxCategory.label);
    } else if (resolved) {
      setDisplayLabel(resolved.label);
    } else if (!row.mappedTaxCategoryId) {
      setDisplayLabel('');
    }
  }, [row.mappedTaxCategory, row.mappedTaxCategoryId, resolved]);

  const scheduleDebounce = useCallback((term: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebouncedSearch(term), DEBOUNCE_MS);
  }, []);

  const shouldSearch = debouncedSearch.length >= 1;
  const { data: results, isLoading } = trpc.taxCategories.search.useQuery(
    { searchValue: debouncedSearch },
    { enabled: open && shouldSearch },
  );

  const handleSelect = (category: TaxCategory) => {
    onChange(category.id);
    setDisplayLabel(category.label);
    setOpen(false);
    setSearch('');
    setDebouncedSearch('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className="crm-bulk-edit-tax-trigger group/trigger"
        >
          {needsResolve && isResolving ? (
            <Loader2 className="size-3.5 shrink-0 animate-spin text-muted-foreground" />
          ) : (
            <span
              className={cn(
                'min-w-0 flex-1 truncate text-left',
                displayLabel ? 'text-foreground' : 'text-muted-foreground/50',
              )}
            >
              {displayLabel || '—'}
            </span>
          )}
          <ChevronDown className="crm-bulk-edit-tax-chevron size-3.5 shrink-0 text-muted-foreground/40" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t('taxCategorySelect.searchPlaceholder')}
            value={search}
            onValueChange={(term) => {
              setSearch(term);
              scheduleDebounce(term);
            }}
          />
          <CommandList>
            <CommandEmpty>
              {isLoading
                ? t('taxCategorySelect.searching')
                : !shouldSearch
                  ? t('taxCategorySelect.typeToSearch')
                  : t('taxCategorySelect.noResults')}
            </CommandEmpty>
            {row.mappedTaxCategoryId ? (
              <CommandGroup>
                <CommandItem
                  onSelect={() => {
                    onChange(null);
                    setDisplayLabel('');
                    setOpen(false);
                  }}
                >
                  <span className="text-muted-foreground">
                    {t('taxCategorySelect.clearSelection')}
                  </span>
                </CommandItem>
              </CommandGroup>
            ) : null}
            {results && results.length > 0 ? (
              <CommandGroup>
                {results.map((category) => (
                  <CommandItem key={category.id} onSelect={() => handleSelect(category as TaxCategory)}>
                    <Check
                      className={cn(
                        'mr-2 size-3.5 shrink-0',
                        row.mappedTaxCategoryId === category.id ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm">{category.label}</div>
                      <div className="text-muted-foreground truncate text-xs">{category.slug}</div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
